import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ListTodo, AlertCircle, FileText,
  Bell, Menu, X, LogOut
} from 'lucide-react';
import { useState } from 'react';
import Avatar from '../ui/Avatar';
import IconButton from '../ui/IconButton';
import { useShop } from '../../hooks/useShop';
import { useDashboard } from '../../hooks/useDashboard';
import { useAuth } from '../../contexts/AuthContext';

const NAV = [
  { to: '/store', end: true, label: 'Home', icon: <LayoutDashboard /> },
  { to: '/store/tasks', label: 'My Tasks', icon: <ListTodo /> },
  { to: '/store/issues', label: 'Issues', icon: <AlertCircle /> },
  { to: '/store/history', label: 'History', icon: <FileText /> },
];

export default function StoreUserShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { currentShop, currentShopId } = useShop();
  const { data: dashboard, loading, refresh } = useDashboard(currentShopId);
  const shopLabel = currentShop?.nickname || currentShop?.name || 'My Shop';
  const displayName = user?.full_name || user?.email || 'Store User';

  return (
    <div className="min-h-screen bg-[var(--color-cream-100)] font-sans">
      {/* Mobile-first topbar */}
      <header className="sticky top-0 z-30 bg-[var(--color-cream-50)] border-b border-[var(--color-warm-100)] px-4 h-14 flex items-center gap-3">
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden -ml-2 w-10 h-10 flex items-center justify-center text-[var(--color-warm-700)] hover:bg-[var(--color-cream-200)] rounded-[var(--radius-md)]"
          aria-label="Open menu"
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)] leading-tight">
            {shopLabel}
          </div>
          <div className="text-[13px] font-bold text-[var(--color-warm-900)] leading-tight truncate">
            Reconciliation Hub
          </div>
        </div>

        <div className="relative">
          <IconButton label="Notifications" variant="ghost">
            <Bell />
          </IconButton>
          <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--color-danger-500)] text-white text-[10px] font-bold flex items-center justify-center pointer-events-none">
            3
          </span>
        </div>

        <Avatar name={displayName} size="sm" />
      </header>

      {/* Mobile slide-out nav */}
      {menuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-[var(--color-warm-900)]/40 animate-fade-in"
          onClick={() => setMenuOpen(false)}
        >
          <nav
            className="bg-[var(--color-cream-50)] w-72 h-full p-4 shadow-[var(--shadow-xl)] animate-slide-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-2 py-3 mb-2 border-b border-[var(--color-warm-100)]">
              <Avatar name={displayName} size="md" />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[var(--color-warm-900)] truncate">{displayName}</div>
                <div className="text-[11px] text-[var(--color-warm-500)] capitalize">Store Manager · {shopLabel}</div>
              </div>
            </div>
            <ul className="space-y-0.5">
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) => `
                      flex items-center gap-3 h-11 px-3
                      rounded-[var(--radius-md)] text-[14px] font-semibold
                      ${isActive
                        ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)]'
                        : 'text-[var(--color-warm-700)] hover:bg-[var(--color-cream-200)]'
                      }
                    `}
                  >
                    <span className="[&_svg]:w-5 [&_svg]:h-5">{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    {item.badge != null && item.badge > 0 && (
                      <span className="text-[10px] font-bold px-1.5 h-5 min-w-[20px] flex items-center justify-center rounded-full bg-[var(--color-action-500)] text-white tabular-nums">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
              <li className="pt-3 mt-3 border-t border-[var(--color-warm-100)]">
                <button
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className="w-full flex items-center gap-3 h-11 px-3 rounded-[var(--radius-md)] text-[14px] font-semibold text-[var(--color-danger-600)] hover:bg-[var(--color-danger-50)]"
                >
                  <LogOut size={20} />
                  <span>Sign out</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Desktop tab nav (shown above content on md+) */}
      <nav className="hidden md:flex border-b border-[var(--color-warm-100)] bg-[var(--color-cream-50)] px-4 overflow-x-auto">
        <div className="max-w-[1000px] mx-auto w-full flex gap-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `
                flex items-center gap-2 h-12 px-4
                text-[13px] font-semibold border-b-2 transition-colors
                ${isActive
                  ? 'border-[var(--color-warm-900)] text-[var(--color-warm-900)]'
                  : 'border-transparent text-[var(--color-warm-500)] hover:text-[var(--color-warm-800)]'
                }
              `}
            >
              <span className="[&_svg]:w-4 [&_svg]:h-4">{item.icon}</span>
              <span>{item.label}</span>
              {item.badge != null && item.badge > 0 && (
                <span className="text-[10px] font-bold px-1.5 h-4 min-w-[16px] flex items-center justify-center rounded-full bg-[var(--color-action-500)] text-white tabular-nums">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      <main className="max-w-[1000px] mx-auto px-4 md:px-6 py-5 md:py-7">
        <Outlet context={{ dashboard, loading, refresh, shopLabel }} />
      </main>
    </div>
  );
}
